/**
 * sharpelab brand identity — a single source of truth for the logo mark and
 * wordmark so the header and auth screens stay visually consistent.
 *
 * The mark is a rising "signal" breakout line with an amber apex spark on a
 * rounded ink tile (matches /public/favicon.svg). The wordmark renders the
 * lowercase "sharpelab" in the display face with an aqua gradient.
 */

interface MarkProps {
  /** Tailwind size/utility classes for the svg element (e.g. "w-9 h-9"). */
  className?: string;
}

export function BrandMark({ className = 'w-9 h-9' }: MarkProps) {
  return (
    <svg
      className={`${className} brand-glow`}
      viewBox="0 0 32 32"
      fill="none"
      role="img"
      aria-label="sharpelab logo"
    >
      <defs>
        <linearGradient id="bm-tile" x1="0" y1="0" x2="0" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#141d2e" />
          <stop offset="1" stopColor="#090d15" />
        </linearGradient>
        <linearGradient id="bm-line" x1="6" y1="23" x2="26" y2="8" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0a9a86" />
          <stop offset="0.55" stopColor="#29d9b9" />
          <stop offset="1" stopColor="#7af0d8" />
        </linearGradient>
      </defs>
      <rect
        x="1"
        y="1"
        width="30"
        height="30"
        rx="8.5"
        fill="url(#bm-tile)"
        stroke="#1f6f64"
        strokeOpacity="0.55"
      />
      <g stroke="#7c89a6" strokeOpacity="0.12" strokeWidth="1">
        <path d="M11 5v22M21 5v22M5 11h22M5 21h22" />
      </g>
      <path
        d="M6 22.5 L12.5 16 L17 18.5 L25.5 8.5"
        stroke="url(#bm-line)"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="25.5" cy="8.5" r="3.4" fill="#fbbf24" stroke="#090d15" strokeWidth="1.2" />
    </svg>
  );
}

interface WordmarkProps {
  className?: string;
}

export function BrandWordmark({ className = 'text-xl' }: WordmarkProps) {
  return (
    <span
      className={`font-display font-extrabold tracking-tight lowercase text-gradient-brand ${className}`}
    >
      sharpelab
    </span>
  );
}

interface BrandLogoProps {
  markClassName?: string;
  wordClassName?: string;
  className?: string;
}

export function BrandLogo({
  markClassName = 'w-9 h-9',
  wordClassName = 'text-xl',
  className = '',
}: BrandLogoProps) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <BrandMark className={markClassName} />
      <BrandWordmark className={wordClassName} />
    </div>
  );
}
